import React, { useState, useEffect } from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { useLocation } from 'react-router-dom'

import { Logo } from '../LandingPage/Logo'

import chromeLogo from '../../media/chrome-logo.png'

const UninstallFeedback = () => {
	const [reason, setReason] = useState('')
	const [comment, setComment] = useState('')
	const [showCommentSection, setShowCommentSection] = useState(false)
	const [submitted, setSubmitted] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [version, setVersion] = useState('')

	const location = useLocation()

	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const versionParam = params.get('version')
		if (versionParam) {
			setVersion(versionParam)
		}
	}, [location.search])

	const handleSubmit = async event => {
		event.preventDefault()
		setIsSubmitting(true)

		const feedbackData = {
			reason,
			comment,
			version,
		}

		try {
			const jwt = localStorage.getItem('jwtToken')

			const response = await fetch('/api/user/uninstall-feedback', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: jwt,
				},
				body: JSON.stringify(feedbackData),
			})

			if (response.ok) {
				setSubmitted(true)
			} else {
				setSubmitted(true)
				throw new Error('Feedback submission failed')
			}
		} catch (error) {
			setSubmitted(true)
			console.error('Error:', error.message)
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<div className="bg-gray-100 min-h-screen">
			<div className="bg-white py-4 shadow">
				<div className="container mx-auto px-4">
					<Logo isLoadboard={false} />
				</div>
			</div>
			<div className="container mx-auto px-4 py-8">
				<div className="max-w-xl mx-auto bg-white p-6 rounded-lg shadow-lg">
					{submitted ? (
						<div className="text-center">
							<CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
							<h1 className="mt-2 text-lg font-semibold text-gray-800">
								Thank you for your feedback!
							</h1>
						</div>
					) : (
						<>
							{!showCommentSection ? (
								<>
									<h1 className="text-xl font-semibold text-gray-800 mb-4">
										We're sad to see you go!
									</h1>
									<p className="text-gray-700 mb-4">
										Please let us know why you decided to uninstall our
										extension:
									</p>
								</>
							) : (
								<div className="flex items-center mb-6">
									<button
										type="button"
										onClick={() => {
											setShowCommentSection(false)
											setReason('')
											setComment('')
										}}
										className="flex items-center text-gray-700 hover:text-gray-900 cursor-pointer"
									>
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											className="mr-2"
										>
											<polyline points="15,18 9,12 15,6"></polyline>
										</svg>
										<span className="text-sm font-bold text-gray-800">
											{reason}
										</span>
									</button>
								</div>
							)}
							<form onSubmit={handleSubmit} className="space-y-4">
								{!showCommentSection ? (
									<>
										<div className="flex items-center">
											<input
												type="radio"
												id="difficulty"
												name="reason"
												value="Difficulty using it"
												className="mr-2"
												onChange={e => {
													setReason(e.target.value)
													setShowCommentSection(true)
												}}
											/>
											<label
												htmlFor="difficulty"
												className="flex-1 text-gray-700"
											>
												I had difficulty using it
											</label>
										</div>
										<div className="flex items-center">
											<input
												type="radio"
												id="features"
												name="reason"
												value="Lack of features"
												className="mr-2"
												onChange={e => {
													setReason(e.target.value)
													setShowCommentSection(true)
												}}
											/>
											<label
												htmlFor="features"
												className="flex-1 text-gray-700"
											>
												It lacked the features I needed
											</label>
										</div>
										<div className="flex items-center">
											<input
												type="radio"
												id="performance"
												name="reason"
												value="Performance issues"
												className="mr-2"
												onChange={e => {
													setReason(e.target.value)
													setShowCommentSection(true)
												}}
											/>
											<label
												htmlFor="performance"
												className="flex-1 text-gray-700"
											>
												Performance issues (e.g., slow, buggy)
											</label>
										</div>
										<div className="flex items-center">
											<input
												type="radio"
												id="price"
												name="reason"
												value="Price over budget"
												className="mr-2"
												onChange={e => {
													setReason(e.target.value)
													setShowCommentSection(true)
												}}
											/>
											<label htmlFor="price" className="flex-1 text-gray-700">
												Price over budget
											</label>
										</div>
										<div className="flex items-center">
											<input
												type="radio"
												id="alternative"
												name="reason"
												value="Found an alternative"
												className="mr-2"
												onChange={e => {
													setReason(e.target.value)
													setShowCommentSection(true)
												}}
											/>
											<label
												htmlFor="alternative"
												className="flex-1 text-gray-700"
											>
												Found an alternative
											</label>
										</div>
										<div className="flex items-center">
											<input
												type="radio"
												id="privacy"
												name="reason"
												value="Privacy concerns"
												className="mr-2"
												onChange={e => {
													setReason(e.target.value)
													setShowCommentSection(true)
												}}
											/>
											<label htmlFor="privacy" className="flex-1 text-gray-700">
												Privacy concerns
											</label>
										</div>
										<div className="flex items-center">
											<input
												type="radio"
												id="other"
												name="reason"
												value="Other"
												className="mr-2"
												onChange={e => {
													setReason(e.target.value)
													setShowCommentSection(true)
												}}
											/>
											<label htmlFor="other" className="flex-1 text-gray-700">
												Other Reasons
											</label>
										</div>
									</>
								) : (
									<div>
										<textarea
											id="comment"
											name="comment"
											rows="10"
											className="w-full p-5 border-2 border-purple-300 rounded-2xl focus:outline-none focus:ring-0 focus:border-purple-400 resize-none text-gray-700 min-h-[200px]"
											placeholder="Please tell us which feature you find hard to use and how can we make it better."
											value={comment}
											onChange={e => setComment(e.target.value)}
										></textarea>
									</div>
								)}

								<button
									type="submit"
									disabled={
										!reason ||
										(showCommentSection && !comment.trim()) ||
										isSubmitting
									}
									style={{
										backgroundColor:
											!reason ||
											(showCommentSection && !comment.trim()) ||
											isSubmitting
												? '#9CA3AF'
												: '#1D4ED8',
										color: 'white',
										padding: '8px 16px',
										width: '100%',
										borderRadius: '0.5rem',
										cursor:
											!reason ||
											(showCommentSection && !comment.trim()) ||
											isSubmitting
												? 'not-allowed'
												: 'pointer',
									}}
									onMouseOver={e => {
										if (!e.currentTarget.disabled) {
											e.currentTarget.style.backgroundColor = '#2563EB'
										}
									}}
									onMouseOut={e => {
										if (!e.currentTarget.disabled) {
											e.currentTarget.style.backgroundColor = '#1D4ED8'
										}
									}}
									className="focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
								>
									{isSubmitting ? (
										<>
											<svg
												className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
											>
												<circle
													className="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"
												></circle>
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												></path>
											</svg>
											Submitting...
										</>
									) : (
										'Submit'
									)}
								</button>
							</form>
						</>
					)}

					<div className="mt-6 text-center text-gray-700 flex items-center justify-center">
						<span>If you change your mind, you can always </span>
						<a
							href="https://chromewebstore.google.com/detail/relay-auto-refresher-post/ihcgicdogclbieclnldfjmlglkolankb"
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center justify-center px-5 py-1 border border-blue-600 text-base font-medium rounded-md text-blue-600 bg-transparent hover:bg-blue-100 ml-2"
						>
							<img
								className="w-6 h-6 mr-2"
								src={chromeLogo}
								alt="Chrome Logo"
							/>
							Reinstall
						</a>
						<span>.</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export default UninstallFeedback
